import { FunctionComponent, memo, useEffect, useState, RefObject } from "react";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  Controller,
} from "react-hook-form";
import { IContentMobile } from "./IContentMobile";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addContentMobile } from "../../Redux/Slices/ContentMobile/AddContentMobileSlice";
import { EditContentMobile } from "../../Redux/Slices/ContentMobile/EditContentMobileSlice";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import ReactQuill, { Quill } from "react-quill";
import NumberInputField from "../../Components/NumberInput/NumberInputField";
import "./ContentMobile.scss";
import "react-quill/dist/quill.snow.css";
import ButtonField from "../../Components/Button/ButtonField";
import TextAreaInputField from "../../Components/TextAreaInput/TextAreaInputField";
import { GetQuestionSubType } from "../../Redux/Slices/QuestionType/GetQuestionSubType";

interface ContentMobileFormProps {
  formRef: RefObject<HTMLFormElement>;
  initialValues: IContentMobile;
  setInitialValues: React.Dispatch<React.SetStateAction<IContentMobile>>;
}

const ContentMobileForm: FunctionComponent<ContentMobileFormProps> = ({
  formRef,
  initialValues,
  setInitialValues,
}) => {
  // Quill modules setup
  const FontSize = ReactQuill.Quill.import("formats/size");
  FontSize.whitelist = ["small", false, "large", "huge"];
  ReactQuill.Quill.register(FontSize, true);

  const Align = ReactQuill.Quill.import("formats/align");
  Quill.register(Align, true);

  const Font = Quill.import("formats/font");
  Font.whitelist = [
    "system",
    "roboto",
    "times-new-roman",
    "courier-new",
    "monospace",
  ];
  Quill.register(Font, true);

  const modules = {
    toolbar: [
      [
        { header: [false, 1, 2, 3, 4, 5, 6] },
        {
          font: [
            "system",
            "roboto",
            "times-new-roman",
            "courier-new",
            "monospace",
          ],
        },
      ],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ script: "sub" }, { script: "super" }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      [{ direction: "rtl" }],
      ["blockquote", "code-block"],
      ["image", "video", "formula"],
      ["clean"],
    ],
    history: {
      delay: 500,
      maxStack: 100,
      userOnly: true,
    },
  };

  const formats = [
    "video",
    "header",
    "font",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "list",
    "bullet",
    "indent",
    "direction",
    "blockquote",
    "code-block",
    "align",
    "image",
    "formula",
  ];

  const dispatch = useAppDispatch();

  const hasMeaningfulText = (html: string) => {
    const text = html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();
    return text.length > 0;
  };

  const schema = z.object({
    id: z.number().optional(),
    name: z.string().min(1, "Please enter name")
    .max(100,"name cannot exceed 100 characters"),
    sub_type_id: z.number({
      required_error: "Please select sub topic",
      invalid_type_error: "Please select sub topic",
    }),
    content: z.string().refine(hasMeaningfulText, {
      message: "Please create the content",
    }),
    description: z
      .string({ message: "Please enter description" })
      .min(1, { message: "Please enter description" })
      .max(500,"name cannot exceed 500 characters"),

    timing: z.number({ message: "Please enter the duration" }),
    status: z.boolean({ message: "Please select the status" }),
    examples: z
      .array(z.string().min(1, "Example cannot be empty"))
      .min(1, "Please add at least one example"),
  });

  const methods = useForm<IContentMobile>({
    defaultValues: {
      ...initialValues,
      status: initialValues.id ? initialValues.status : true,
      examples: initialValues.examples ?? [],
      timing: initialValues.timing ? Number(initialValues.timing) : undefined, // 👈 Convert to number
    },
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const [editorValue, setEditorValue] = useState("");
  const [exampleEditor, setExampleEditor] = useState("");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (initialValues?.content) {
      setEditorValue(initialValues.content);
      methods.setValue("content", initialValues.content);
    }
    return () => {
      setInitialValues({});
    };
  }, [initialValues.content]);

  // const onSubmit: SubmitHandler<IContentMobile> = (val) => {
  //   console.log(val);

  //   if (val.id) {
  //     dispatch(EditContentMobile(val));
  //   } else {
  //     dispatch(addContentMobile(val));
  //   }
  // };

  const onSubmit: SubmitHandler<IContentMobile> = (val) => {
    const payload = {
      ...val,
      timing: val.timing?.toString() ?? "", // force string
    };

    console.log(payload);

    if (payload.id) {
      dispatch(EditContentMobile(payload));
    } else {
      dispatch(addContentMobile(payload));
    }
  };
  useEffect(() => {
    dispatch(GetQuestionSubType());
  }, []);
  const { data: listQuestionSubTypes } = useAppSelector(
    (state) => state.List_Question_SubTypeSliceReducer //List_Question_SubTypeSliceReducer
  );
  // const { data: ListSubjectList } = useAppSelector(
  //   (state) => state.ListSubjectListSliceReducer
  // );
  // const { data: GetQuestionSubType } = useAppSelector(
  //   (state) => state.GetQuestionSubTypeSliceReducer
  // );
  // const { data: listQuestionType } = useAppSelector(
  //   (state) => state.ListQuestionSubTypeSlice
  // );

  const SubList = Array.isArray(listQuestionSubTypes?.data)
    ? listQuestionSubTypes?.data
        .filter((item: any) => item.status !== false)
        .map((item: any) => ({
          label: item.full_name!,
          value: item.id,
        }))
    : [];

  const ContentMobileStatus = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
  ];

  const handleAddExample = () => {
    const plainText = exampleEditor;
    if (!plainText) return;

    const current: any = methods.getValues("examples");
    if (editingIndex !== null) {
      current[editingIndex] = plainText;
      setEditingIndex(null);
    } else {
      current.push(plainText);
    }
    methods.setValue("examples", [...current], { shouldValidate: true });
    setExampleEditor("");
  };

  const handleEditExample = (index: number) => {
    const examples: any = methods.getValues("examples");
    setExampleEditor(examples[index]);
    setEditingIndex(index);
  };

  const handleDeleteExample = (index: number) => {
    const current: any = methods?.getValues("examples");
    const updated = current?.filter((_: any, i: any) => i !== index);
    methods.setValue("examples", updated, { shouldValidate: true });
    if (editingIndex === index) {
      setExampleEditor("");
      setEditingIndex(null);
    }
  };

  return (
    <FormProvider {...methods}>
      <form ref={formRef} onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="form_double_input">
          <TextInputFieldC name="name" label="Name" type="text" />
          <Select
            name="sub_type_id"
            label="Select the sub type"
            options={SubList}
            value={
              SubList?.find(
                (option: any) =>
                  option.value === methods.getValues("sub_type_id")
              ) ?? null
            }
            onChange={(_, value) => {
              const val = value as AutocompleteOption;
              methods.setValue("sub_type_id", val?.value, {
                shouldValidate: true,
              });
            }}
          />
          <NumberInputField
            name="timing"
            label="Duration in Minutes"
            type="number"
            sx={{ width: "100%" }}
          />
          <TextAreaInputField
            name="description"
            label="Description"
            type="text"
            minRows={1}
            maxRows={6}
            sx={{ width: "100%" }}
          />
        </div>

        {/* Content Editor */}
        <ReactQuill
          className="custom-quill"
          style={{ width: "100%", paddingTop: "20px", borderRadius: "10px" }}
          theme="snow"
          value={editorValue}
          onChange={(content) => {
            setEditorValue(content);
            methods.setValue("content", content, { shouldValidate: true });
          }}
          modules={modules}
          formats={formats}
          placeholder="Create content here..."
        />

        {methods.formState.errors.content && (
          <p style={{ color: "#BD0000" }}>
            {methods.formState.errors.content.message}
          </p>
        )}

        {/* Status */}
        <Select
          name="status"
          label="Status"
          sx={{ marginTop: "20px" }}
          options={ContentMobileStatus}
          value={
            ContentMobileStatus.find(
              (opt) => opt.value === methods.getValues("status")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("status", val?.value, { shouldValidate: true });
          }}
        />

        {/* Examples */}
        <div style={{ marginTop: "30px" }}>
          <h4>Add Example</h4>
          <ReactQuill
            className="custom-quill"
            style={{
              width: "100%",
              borderRadius: "10px",
              marginBottom: "20px",
            }}
            theme="snow"
            value={exampleEditor}
            onChange={setExampleEditor}
            placeholder=""
            modules={modules}
            formats={formats}
          />

          {/* <ReactQuill
          className="custom-quill"
          style={{ width: "100%", paddingTop: "20px", borderRadius: "10px" }}
          theme="snow"
          value={editorValue}
          onChange={(content) => {
            setEditorValue(content);
            methods.setValue("content", content, { shouldValidate: true });
          }}
          modules={modules}
          formats={formats}
          placeholder="Create content here..."
        /> */}

          <ButtonField
            className="form_save_button"
            type="button"
            onClick={handleAddExample}
          >
            {editingIndex !== null ? "Update Example" : "Add Example"}
          </ButtonField>

          {methods.formState.errors.examples && (
            <p style={{ color: "#BD0000" }}>
              {methods.formState.errors.examples.message}
            </p>
          )}

          <div style={{ paddingTop: "20px" }}>
            {methods.getValues("examples")?.map((example, index) => (
              <div
                key={index}
                style={{
                  marginBottom: "20px",
                  border: "1px solid #ccc",
                  borderRadius: "10px",
                  padding: "10px",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <ReactQuill
                  value={example}
                  readOnly
                  theme="bubble"
                  modules={{ toolbar: false }}
                  style={{
                    borderRadius: "8px",
                    padding: "0px",
                    minHeight: "80px",
                  }}
                />

                <div style={{ marginTop: "10px" }}>
                  <ButtonField
                    className="form_save_button"
                    type="button"
                    onClick={() => handleEditExample(index)}
                    sx={{ marginRight: "8px" }}
                  >
                    Edit
                  </ButtonField>
                  <ButtonField
                    className="form_save_button"
                    type="button"
                    onClick={() => handleDeleteExample(index)}
                  >
                    Delete
                  </ButtonField>
                </div>
              </div>
            ))}
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default memo(ContentMobileForm);
